import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { ApiProblem, asProblem, isAbort } from './problem.js';

/**
 * Small hooks shared by the pages.
 *
 * Kept in one file because each is a handful of lines and because they share a
 * single concern: making the loading/error/empty triad uniform, so that no page
 * invents its own slightly-different version of "still loading".
 */

/* -------------------------------------------------------------------------- */
/*  Reading                                                                   */
/* -------------------------------------------------------------------------- */

export interface AsyncState<T> {
  data: T | null;
  error: ApiProblem | null;
  /** True during the first load and during every reload, so the UI can say so. */
  loading: boolean;
  /** Re-runs the loader. Safe to call from an event handler. */
  reload: () => void;
}

/**
 * Loads data on mount and whenever `deps` change.
 *
 * ## Why it takes an `AbortSignal`
 *
 * Every request is abortable, and the cleanup function aborts the in-flight one.
 * Without that, a user who clicks through four organisation tabs quickly leaves
 * three requests running, and whichever finishes last wins — so the page can
 * settle showing the members of an organisation the user already navigated away
 * from. The `active` flag alone would prevent the *state update* but the request
 * would still be made; aborting prevents both, and saves the bandwidth.
 *
 * ## Why there is no `setState` after unmount warning
 *
 * React 18 removed it, but the underlying bug it warned about is real: an
 * `active` flag set to false in cleanup is what stops a resolved promise from
 * writing to an unmounted component's state.
 *
 * ## Why the loader is held in a ref
 *
 * The loader is usually an inline arrow function, so it is a new reference on
 * every render. Putting it in the dependency array would refetch forever; leaving
 * it out entirely would capture a stale closure. Holding it in a ref that is
 * updated by an effect declared *before* the load effect gets both: the deps
 * array stays under the caller's control, and the loader is always current.
 */
export function useAsync<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): AsyncState<T> {
  const [state, setState] = useState<{ data: T | null; error: ApiProblem | null; loading: boolean }>({
    data: null,
    error: null,
    loading: true,
  });
  const [nonce, setNonce] = useState(0);

  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState((previous) => ({ ...previous, loading: true }));

    loadRef
      .current(controller.signal)
      .then((data) => {
        if (!active) return;
        setState({ data, error: null, loading: false });
      })
      .catch((error: unknown) => {
        // An abort is the expected outcome of navigating away. Treating it as an
        // error would flash a failure banner during ordinary navigation.
        if (!active || isAbort(error)) return;
        setState({ data: null, error: asProblem(error), loading: false });
      });

    return () => {
      active = false;
      controller.abort();
    };
    // The caller owns the dependency list. See the comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { ...state, reload };
}

/* -------------------------------------------------------------------------- */
/*  Writing                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The outcome of a mutation.
 *
 * An explicit discriminated union rather than a thrown exception or a nullable
 * result. Reading `state.error` immediately after `await run()` gives the value
 * from the *previous* render — React has not re-rendered yet — which is the
 * single most common bug in this shape of code. Returning the outcome makes it
 * impossible to read a stale one.
 */
export type ActionResult<T> = { ok: true; data: T } | { ok: false; error: ApiProblem };

export interface AsyncAction<Args extends readonly unknown[], T> {
  run: (...args: Args) => Promise<ActionResult<T>>;
  pending: boolean;
  /** The most recent failure, for rendering a banner next to the control. */
  error: ApiProblem | null;
  reset: () => void;
}

/**
 * Runs a mutation, tracking pending state and the last error.
 *
 * ## Why `pending` is a counter, not a boolean
 *
 * Two buttons can share one action — "Revoke" in a row and "Revoke" in a
 * confirmation dialog both call the same handler. With a boolean, whichever
 * finishes first clears the flag while the other is still in flight, and both
 * buttons re-enable. A counter only reaches zero when the last one settles.
 */
export function useAsyncAction<Args extends readonly unknown[], T>(
  action: (...args: Args) => Promise<T>,
): AsyncAction<Args, T> {
  const [pending, setPending] = useState(0);
  const [error, setError] = useState<ApiProblem | null>(null);

  // Prevents a state update after unmount, which is what an aborted navigation
  // during a slow mutation would otherwise cause.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const actionRef = useRef(action);
  useEffect(() => {
    actionRef.current = action;
  });

  const run = useCallback(async (...args: Args): Promise<ActionResult<T>> => {
    setPending((value) => value + 1);
    setError(null);
    try {
      const data = await actionRef.current(...args);
      if (mounted.current) setError(null);
      return { ok: true, data };
    } catch (caught: unknown) {
      const problem = asProblem(caught);
      if (mounted.current) setError(problem);
      return { ok: false, error: problem };
    } finally {
      if (mounted.current) setPending((value) => value - 1);
    }
  }, []);

  const reset = useCallback(() => setError(null), []);

  return { run, pending: pending > 0, error, reset };
}

/* -------------------------------------------------------------------------- */
/*  Input                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * A value that trails its input by `delay` milliseconds.
 *
 * Used for the audit log's filter inputs. Without it, every keystroke in the
 * action filter is a request, and the audit log is the most expensive read in the
 * API.
 */
export function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

/**
 * Copy to clipboard, with a short "copied" flag.
 *
 * `navigator.clipboard` is unavailable on an insecure origin and can reject
 * outright when the document is not focused, so the failure is handled rather
 * than assumed away — the caller shows the value in a selectable field instead.
 */
export function useCopy(resetAfterMs = 2000): {
  copied: boolean;
  failed: boolean;
  copy: (value: string) => Promise<void>;
} {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, []);

  const copy = useCallback(
    async (value: string) => {
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setFailed(false);
      } catch {
        setCopied(false);
        setFailed(true);
      }

      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        setCopied(false);
        setFailed(false);
      }, resetAfterMs);
    },
    [resetAfterMs],
  );

  return { copied, failed, copy };
}

/* -------------------------------------------------------------------------- */
/*  Document                                                                  */
/* -------------------------------------------------------------------------- */

/** Sets `document.title`, and restores nothing — the next page sets its own. */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    const previous = document.title;
    document.title = title.length > 0 ? `${title} · Keystone` : 'Keystone Console';
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/**
 * Calls the handler when Escape is pressed.
 *
 * Attached to `document` rather than to the dialog element, because the focus
 * may be inside a portalled overlay that is not a DOM descendant of the trigger.
 * Only active while `enabled`, so a closed dialog does not swallow Escape.
 */
export function useEscape(enabled: boolean, handler: () => void): void {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        handlerRef.current();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}

/**
 * Moves focus to an element when a condition becomes true.
 *
 * A dialog that opens without moving focus leaves the keyboard user behind it,
 * tabbing through the page underneath. Focus management is not decoration.
 */
export function useAutoFocus<T extends HTMLElement>(active: boolean): RefObject<T | null> {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!active) return;
    // After paint, so the element exists and is focusable.
    const frame = window.requestAnimationFrame(() => ref.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [active]);

  return ref;
}
